import { useCallback, useEffect, useState } from 'react';

import Axios from '../../../Assets/Axios/Axios';

import { toCapitalize, toCurrencyString } from '../../../Helpers';

import { FindUserOrdersByOrderStatus } from '../../../Server/Query';

/**
 *@param{string} userId
 *@param{Array<string>} orderStatus
 *@returns {{
  updateOrders:()=>void,
  hasErrored:boolean,
  isLoading:boolean,
  orders:{
    id: string;
    dateTime: string;
    note: string;
    referenceId: string;
    place: string;
    status: string;
    professional: {
      id:string;
      fullName: string;
      mobileNumber: string;
    }
    service:{
      id: string;
      name: string;
    }
    package: {
        id: string;
        name: string;
        price: string;
    }
  }[]
 }}[]
 */

function useUserOrders(userId, orderStatus) {
  const [orders, setOrders] = useState([]);
  const [hasErrored, setHasErrored] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    if (!userId) return undefined;
    setIsLoading(true);
    setHasErrored(false);
    const query = FindUserOrdersByOrderStatus({ orderStatus, userID: userId });
    const { data, errors } = await Axios(query);

    setIsLoading(false);
    if (errors) {
      setHasErrored(true);
      return undefined;
    }

    setOrders((data?.Orders ?? []).map(parseOrder));
  }, [orderStatus, userId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return { orders, updateOrders: fetchOrders, hasErrored, isLoading };
}

function parseOrder(order) {
  const servicePackage = order.ServicePackage ?? order.servicePackage ?? {};
  const service = servicePackage.Service ?? servicePackage.service ?? {};
  const professional = service.Professional ?? order.professional ?? {};

  const professionalFullName =
    [professional.first_name ?? professional.firstName, professional.last_name ?? professional.lastName]
      .filter(Boolean)
      .join(' ') || 'Not available';

  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    timeStyle: 'short',
  });

  return {
    id: order.id,
    referenceId: order.reference_id ?? order.referenceId ?? order.orderRef,
    place: order.place ?? service.location ?? '',
    dateTime: order.date
      ? `${order.date.split('T')[0]} ${order.time ? timeFormatter.format(new Date(order.time)) : ''}`
      : '',
    note: order.note,
    status: toCapitalize(order.status ?? ''),
    package: {
      id: servicePackage.id,
      name: servicePackage.name,
      price: servicePackage.price ? toCurrencyString(servicePackage.price) : '',
    },
    professional: {
      id: professional.id,
      fullName: professionalFullName,
      mobileNumber: professional.mobile_number ?? professional.mobileNumber,
    },
    service: {
      id: service.id,
      name: service.name ? toCapitalize(service.name) : 'Not available',
    },
  };
}

export default useUserOrders;
